import { GridState, StateUpdatedEvent } from '@ag-grid-community/core';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { useStore } from '../../store/store';
import { useCallback } from 'react';
import { convertOldUrlState } from '../../utils/old-url-state-conversion';

export const stateKey = 'state';
export const searchKey = 'search';

// Some events are triggered by the grid initialization or sidebar opening, we don't want to store those states
const eventsToIgnore = ['gridInitializing', 'sideBar', 'columnSizing', 'scroll'];

export function useUrlTableState() {
  const notifyUrlTick = useStore(state => state.notifyUrlTick);

  function updateParam(key: string, value: string | null | undefined) {
    const next = new URLSearchParams(window.location.search);
    if (next.get(key) === value) return; // Avoid unnecessary updates
    if (value === null || value === undefined || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    const base = location.pathname + location.hash;
    const query = next.toString();
    const newUrl = query ? `${base}?${query}` : base;
    // Not using useSearchParams so we don't trigger another render
    // as it messes with the ag-grid lifecycle
    window.history.replaceState(null, '', newUrl);
  }

  function storeSearch(search: string) {
    const updatedSearch = encodeURIComponent(search);
    updateParam(searchKey, updatedSearch);
    notifyUrlTick();
  }

  function getSearch(): string {
    const searchParams = new URLSearchParams(window.location.search);
    const search = searchParams.get(searchKey);
    if (!search) return '';
    return decodeURIComponent(search);
  }

  const storeGridState = useCallback(
    (event: StateUpdatedEvent) => {
      if (event && !eventsToIgnore.some(source => event.sources.includes(source))) {
        updateParam(stateKey, compressToEncodedURIComponent(JSON.stringify(event.state)));
        notifyUrlTick();
      }
    },
    [notifyUrlTick]
  );

  function getGridState(): GridState | undefined {
    const searchParams = new URLSearchParams(window.location.search);
    const compressedState = searchParams.get(stateKey);
    if (!compressedState) {
      return convertOldUrlState();
    }

    try {
      const stateString = decompressFromEncodedURIComponent(compressedState);
      return JSON.parse(stateString) as GridState;
    } catch (e) {
      console.error('Failed to parse grid state from URL params', e);
      return undefined;
    }
  }

  function clearGridState() {
    window.history.replaceState({}, '', window.location.pathname);
    location.reload();
  }

  const initialState = getGridState();

  return { initialState, storeGridState, clearGridState, storeSearch, getSearch };
}
